import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api from "../../lib/api";
import { toast } from "sonner";
import {
  ArrowLeft,
  Ban,
  CalendarRange,
  CheckCircle,
  Mail,
  Phone,
  ShieldOff,
  Wallet,
} from "@/components/icons";

const DAY_MS = 24 * 60 * 60 * 1000;

/** Tuesday on or before the given date (week start). Week = Tue → Mon. */
function weekStartTue(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  const day = x.getDay();
  const daysSinceTue = (day - 2 + 7) % 7;
  x.setDate(x.getDate() - daysSinceTue);
  return x;
}
const fmtShort = (d) =>
  d.toLocaleDateString("es-ES", { day: "numeric", month: "short" });

const initials = (n) =>
  (n || "")
    .split(" ")
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
const money = (n) => `$${(n || 0).toFixed(2)}`;

export default function PharmacyOrDoctorDetail({ kind = "pharmacy" }) {
  // kind: "pharmacy" or "doctor" — same UI, slightly different copy + action.
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () =>
    api.get(`/users/${id}/billing`).then((r) => setData(r.data));

  useEffect(() => {
    load();
    const t = setInterval(load, 5000); // realtime polling
    return () => clearInterval(t);
  }, [id]);

  // Compute weekly buckets BEFORE any early return so hooks run in stable order.
  const isPharm = kind === "pharmacy";
  const sales = data?.sales || [];
  const weeks = useMemo(() => {
    const buckets = {};
    for (const s of sales) {
      const start = weekStartTue(s.created_at);
      const k = start.toISOString().slice(0, 10);
      if (!buckets[k]) {
        buckets[k] = {
          key: k,
          start,
          end: new Date(start.getTime() + 6 * DAY_MS),
          payout: new Date(start.getTime() + 7 * DAY_MS),
          sales: [],
          amount: 0,
          pending: 0,
          paid: 0,
        };
      }
      const b = buckets[k];
      const amt = isPharm
        ? s.platform_commission_amount || 0
        : s.doctor_amount || 0;
      const isPaid = isPharm ? s.pharmacy_paid : s.doctor_paid;
      b.sales.push(s);
      b.amount += amt;
      if (isPaid) b.paid += amt;
      else b.pending += amt;
    }
    return Object.values(buckets).sort((a, b) => b.start - a.start);
  }, [sales, isPharm]);

  if (!data) {
    return <div className="p-10 text-black">Cargando…</div>;
  }

  const u = data.user;
  const t = data.totals;
  const pendingAmt = isPharm ? t.pending_commission : t.pending_doctor;
  const paidAmt = isPharm ? t.paid_commission : t.paid_doctor;

  const settle = async () => {
    if (pendingAmt <= 0) {
      toast.info("Nada pendiente para liquidar");
      return;
    }
    const verb = isPharm ? "Cobrar" : "Pagar";
    if (
      !window.confirm(
        `${verb} ${money(pendingAmt)} a ${u.name}? Se marcarán como ${isPharm ? "cobradas" : "pagadas"} todas sus ventas pendientes.`,
      )
    )
      return;
    setBusy(true);
    try {
      const r = await api.post(
        `/pharmacy/users/${id}/settle?kind=${kind}`,
      );
      toast.success(
        `Listo — ${r.data.updated} venta${r.data.updated === 1 ? "" : "s"} liquidada${r.data.updated === 1 ? "" : "s"}`,
      );
      load();
    } catch {
      toast.error("Error al liquidar");
    } finally {
      setBusy(false);
    }
  };

  const settleWeek = async (w) => {
    if (w.pending <= 0) {
      toast.info("Esta semana ya está liquidada");
      return;
    }
    const verb = isPharm ? "Cobrar" : "Pagar";
    if (
      !window.confirm(
        `${verb} ${money(w.pending)} de la semana ${fmtShort(w.start)} → ${fmtShort(w.end)}?`,
      )
    )
      return;
    const startStr = w.start.toISOString().slice(0, 10);
    const endStr = w.end.toISOString().slice(0, 10);
    try {
      const r = await api.post(
        `/pharmacy/users/${id}/settle-week?kind=${kind}&start=${startStr}&end=${endStr}`,
      );
      toast.success(
        `Semana liquidada — ${r.data.updated} venta${r.data.updated === 1 ? "" : "s"}`,
      );
      load();
    } catch {
      toast.error("Error al liquidar la semana");
    }
  };

  const toggleBlock = async () => {
    const next = !u.blocked;
    if (
      !window.confirm(
        next
          ? `Bloquear a ${u.name}? No podrá iniciar sesión hasta que la desbloquees.`
          : `Desbloquear a ${u.name}? Podrá iniciar sesión de nuevo.`,
      )
    )
      return;
    try {
      await api.post(`/users/${id}/${next ? "block" : "unblock"}`);
      toast.success(next ? "Bloqueado" : "Desbloqueado");
      load();
    } catch {
      toast.error("Error");
    }
  };

  return (
    <div className="space-y-6" data-testid="pharm-doc-detail">
      <Link
        to="/admin/pagos-farmacia"
        className="inline-flex items-center gap-1.5 text-sm font-bold text-black/55 hover:text-black"
      >
        <ArrowLeft className="w-4 h-4" /> Volver a Pagos de farmacias
      </Link>

      {/* Header */}
      <div className="bg-white rounded-[28px] border border-black/[0.06] p-6 flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-full bg-white border border-[#E5E7EB] text-black text-base font-extrabold flex items-center justify-center">
            {initials(u.name)}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-extrabold text-black tracking-tight">
                {u.name}
              </h1>
              {u.blocked && (
                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-extrabold">
                  <Ban className="w-3 h-3" /> Bloqueado
                </span>
              )}
            </div>
            <div className="text-xs text-black/60 flex flex-wrap gap-3 mt-1">
              <span className="inline-flex items-center gap-1">
                <Mail className="w-3 h-3" /> {u.email}
              </span>
              {u.phone && (
                <span className="inline-flex items-center gap-1">
                  <Phone className="w-3 h-3" /> {u.phone}
                </span>
              )}
              <span className="inline-flex items-center gap-1">
                <span className="px-2 py-0.5 rounded-full bg-black/[0.06] text-black text-[10px] font-extrabold uppercase tracking-widest">
                  {isPharm ? "Farmacia" : "Doctor"}
                </span>
              </span>
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={settle}
            disabled={busy || pendingAmt <= 0}
            data-testid="settle-button"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#01BC8E] text-black font-extrabold text-sm hover:bg-[#16B58D] transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Wallet className="w-4 h-4" />
            {isPharm ? `Cobrar ${money(pendingAmt)}` : `Pagar ${money(pendingAmt)}`}
          </button>
          <button
            onClick={toggleBlock}
            data-testid="block-button"
            className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-bold text-sm transition border ${
              u.blocked
                ? "bg-[#01BC8E] text-white hover:bg-[#019A75] border-transparent"
                : "bg-white text-red-600 hover:bg-red-50 border-red-200"
            }`}
          >
            {u.blocked ? (
              <>
                <CheckCircle className="w-4 h-4" /> Desbloquear
              </>
            ) : (
              <>
                <ShieldOff className="w-4 h-4" /> Bloquear
              </>
            )}
          </button>
        </div>
      </div>

      {/* Totals */}
      <div className="grid sm:grid-cols-3 gap-4">
        <Metric title="Volumen total" value={money(t.total_volume)} />
        <Metric
          title={isPharm ? "Pendiente cobrar" : "Pendiente pagar"}
          value={money(pendingAmt)}
          tone="amber"
        />
        <Metric
          title={isPharm ? "Ya cobrado" : "Ya pagado"}
          value={money(paidAmt)}
          tone="emerald"
        />
      </div>

      {/* Weekly settlements */}
      <div className="bg-white rounded-[28px] border border-black/[0.06] p-6" data-testid="weeks-section">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div>
            <div className="text-lg font-extrabold text-black flex items-center gap-2">
              <CalendarRange className="w-5 h-5 text-black" /> Liquidaciones semanales
            </div>
            <div className="text-xs text-black/55">
              Cada semana corre de martes a lunes. El pago se procesa el martes siguiente.
            </div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left">
                {[
                  "Semana",
                  "Pago programado",
                  "Ventas",
                  isPharm ? "Comisión" : "A pagar",
                  "Estado",
                  "",
                ].map((h, i) => (
                  <th
                    key={h || `c${i}`}
                    className="py-3 px-3 first:pl-0 last:pr-0 text-[11px] uppercase tracking-widest text-[#9CA3AF] font-medium"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {weeks.map((w) => (
                <tr
                  key={w.key}
                  data-testid={`week-row-${w.key}`}
                  className="border-t border-[#E5E7EB] text-black hover:bg-black/[0.03] transition"
                >
                  <td className="py-3 pr-3 whitespace-nowrap font-bold">
                    {fmtShort(w.start)} → {fmtShort(w.end)}
                  </td>
                  <td className="py-3 px-3 text-black/85 whitespace-nowrap">
                    {fmtShort(w.payout)}
                  </td>
                  <td className="py-3 px-3 text-black/85">{w.sales.length}</td>
                  <td className="py-3 px-3 font-extrabold whitespace-nowrap">
                    <div>{money(w.amount)}</div>
                    {w.pending > 0 && (
                      <div className="text-[11px] text-amber-700 font-bold">
                        pendiente: {money(w.pending)}
                      </div>
                    )}
                  </td>
                  <td className="py-3 px-3">
                    {w.pending <= 0 ? (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-blue-50 text-[#019A75] font-extrabold">
                        <CheckCircle className="w-3 h-3" /> Liquidada
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-extrabold">
                        Pendiente
                      </span>
                    )}
                  </td>
                  <td className="py-3 pl-3 text-right">
                    {w.pending > 0 && (
                      <button
                        onClick={() => settleWeek(w)}
                        data-testid={`settle-week-${w.key}`}
                        className={`inline-flex items-center gap-1 text-xs font-bold px-3 py-1.5 rounded-full transition ${
                          isPharm
                            ? "bg-[#5B7AFF] text-white hover:bg-[#4A66E5]"
                            : "bg-[#01BC8E] text-white hover:bg-[#019A75]"
                        }`}
                      >
                        {isPharm
                          ? `Cobrar ${money(w.pending)}`
                          : `Pagar ${money(w.pending)}`}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {weeks.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center text-black/55 py-10">
                    Sin liquidaciones aún
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Sales table */}
      <div className="bg-white rounded-[28px] border border-black/[0.06] p-6">
        <div className="mb-4">
          <div className="text-lg font-extrabold text-black">
            Ventas registradas
          </div>
          <div className="text-xs text-black/55">
            {data.sales.length} venta{data.sales.length === 1 ? "" : "s"} en
            total
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left">
                {[
                  "Fecha",
                  isPharm ? "Doctor" : "Farmacia",
                  "Venta",
                  "Comisión",
                  "Doctor recibe",
                  "Estado",
                ].map((h) => (
                  <th
                    key={h}
                    className="py-3 px-3 first:pl-0 last:pr-0 text-[11px] uppercase tracking-widest text-[#9CA3AF] font-medium"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.sales.map((s) => (
                <tr
                  key={s.id}
                  className="border-t border-[#E5E7EB] text-black hover:bg-black/[0.03] transition"
                >
                  <td className="py-3 pr-3 whitespace-nowrap text-black/85">
                    {new Date(s.created_at).toLocaleDateString("es-ES", {
                      day: "numeric",
                      month: "short",
                      year: "numeric",
                    })}
                  </td>
                  <td className="py-3 px-3 text-black/85 truncate max-w-[260px]">
                    {isPharm ? s.doctor_id?.slice(0, 8) : s.pharmacy_id?.slice(0, 8)}
                  </td>
                  <td className="py-3 px-3 font-bold whitespace-nowrap">
                    {money(s.total_amount)}
                  </td>
                  <td className="py-3 px-3 text-black font-bold whitespace-nowrap">
                    {money(s.platform_commission_amount)}
                  </td>
                  <td className="py-3 px-3 text-[#019A75] font-bold whitespace-nowrap">
                    {money(s.doctor_amount)}
                  </td>
                  <td className="py-3 pl-3">
                    {isPharm ? (
                      s.pharmacy_paid ? (
                        <span className="text-[#019A75] text-[10px] font-extrabold uppercase tracking-widest">
                          Cobrada
                        </span>
                      ) : (
                        <span className="text-amber-700 text-[10px] font-extrabold uppercase tracking-widest">
                          Pendiente
                        </span>
                      )
                    ) : s.doctor_paid ? (
                      <span className="text-[#019A75] text-[10px] font-extrabold uppercase tracking-widest">
                        Pagada
                      </span>
                    ) : (
                      <span className="text-amber-700 text-[10px] font-extrabold uppercase tracking-widest">
                        Pendiente
                      </span>
                    )}
                  </td>
                </tr>
              ))}
              {data.sales.length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    className="text-center text-black/55 py-10"
                  >
                    Sin ventas registradas
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Metric({ title, value, tone }) {
  const tones = {
    amber: "text-amber-700",
    emerald: "text-[#019A75]",
  };
  return (
    <div className="bg-white rounded-[28px] border border-black/[0.06] p-5">
      <div className="text-[11px] uppercase tracking-widest font-bold text-black/55">
        {title}
      </div>
      <div className={`text-3xl font-extrabold mt-2 ${tones[tone] || "text-black"}`}>
        {value}
      </div>
    </div>
  );
}
